// in src/Foo.js
import * as React from "react";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Grid from "@material-ui/core/Grid";
import { Title, useListContext } from "react-admin";
import API from "../../helpers/apiHelpers";
import { Autocomplete } from "@material-ui/lab";
import {TextField, Typography, Button, CircularProgress, Box, createMuiTheme} from "@material-ui/core";
import {states} from "../../Constants/locations";
import { DateTimePicker } from "@material-ui/pickers";
import moment from "moment";
import { timezones } from "../../Constants/timezones";
import { useSnackbar } from "notistack";
import {ThemeProvider} from "@material-ui/styles";
import {
    colorBlack,
    colorError,
    colorInputBorder, colorInputBorderGrayish,
    colorPrimary,
    colorSecondary,
    colorWhite
} from "../../Constants/theme";
import { clear, profile } from "console";

const servicesList = ["ADVICE_CHAT", "ADVICE_VIDEO", "RX_CHAT", "RX_VIDEO", "IMMEDIATE_CHAT", "IMMEDIATE_CALL"];
const materialTheme = createMuiTheme({
    palette: {
        common: {
            white: colorWhite,
            black: colorBlack,
        },
        primary: {
            main: colorPrimary,
        },
        secondary: {
            main: colorSecondary,
        },
        error: {
            main: colorError,
        },
        text: {
            primary: colorPrimary,
            secondary: colorInputBorder
        }
    },
    typography: {
        allVariants: {
            fontFamily: "Inter",
            lineHeight: "26px",
            color: colorPrimary,
        },
        h2: {
            fontFamily: "NotoSerifDisplay",
            fontSize: 28,
            fontWeight: "bold",
            lineHeight: "35px",
            color: colorPrimary,
        },
        h3: {
            fontWeight: "bold",
            fontSize: 24,
            color: colorPrimary,
        },
        h4: {
            fontWeight: "bold",
            fontSize: 20,
            color: colorPrimary,
        },
        body2: {
            fontSize: 16,
            fontWeight: 700,
        },
        button: {
            fontWeight: "bold",
            fontSize: 16,
            textTransform: "none",
            color: colorPrimary,
        }
    },
    overrides: {
        MuiPaper: {
            root: {
                backgroundColor: "#1e1e1e",
            },
        },
        MuiButton: {
            root: {
                height: 24,
                color: "#fff",
                "&$disabled": {
                    backgroundColor: colorInputBorderGrayish + " !important",
                    border: "1.5px solid " + colorInputBorder,
                    borderRadius: "6px",
                },
            },
            containedPrimary: {
                color: colorPrimary,
                backgroundColor: colorSecondary,
                border: colorSecondary,
                "&:hover": {
                    color: colorWhite,
                    backgroundColor: colorSecondary,
                },
            },
            containedSecondary: {
                border: colorSecondary,
                color: colorPrimary,
                "&:hover": {
                    color: colorWhite,
                },
            },
            contained: {
                backgroundColor: colorWhite,
                color: colorInputBorder,
                border: "1.5px solid " + colorInputBorder,
                borderRadius: "6px",
                boxShadow: "none"
            }
        },
        MuiInput: {
            root: {
                borderWidth: 1
            },
        },
        MuiIconButton: {
            root: {
                padding: 0,
            },
        },
        MuiInputAdornment: {
            positionEnd: {
                marginLeft: 8,
            },
        },
        MuiOutlinedInput: {
            root: {
                "&$disabled": {
                    color: colorInputBorder,
                },
            },
            notchedOutline: {
                borderColor: colorInputBorder,
            },
            adornedEnd: {
                display: "flex",
                flexDirection: "row-reverse",
                paddingLeft: "14px",
                paddingRight: 0,
            },
        },
        MuiSelect: {
            root: {
                width: 241,
                height: 48
            }
        },
        MuiFormControlLabel: {
            label: {
                "&$disabled": {
                    color: colorInputBorder,
                }
            }
        }
    }
});

const ScheduleAppointment = (props) => {
    const {data} = useListContext();
    const [profiles, setProfiles] = React.useState<undefined | any>([]);
    const [user, setUser] = React.useState<undefined | any>();
    const [animals, setAnimals] = React.useState<undefined | any>();
    const [animal, setAnimal] = React.useState<undefined | any>();
    const [selectedState, setSelectedState] = React.useState<undefined | any>();
    const [serviceType, setServiceType] = React.useState<string>("");
    const [vets, setVets] = React.useState<undefined | any>();
    const [vet, setVet] = React.useState<undefined | any>();
    const [time, setTime] = React.useState(moment.utc().startOf("hour"));
    const [description, setDescription] = React.useState<string>("");
    const [timeZone, setTimeZone] = React.useState<string>("UTC");
    const [queryProfile, setQueryProfile] = React.useState<string>("");
    const {enqueueSnackbar} = useSnackbar();
    const [wrongAnimal, setWrongAnimal] = React.useState<boolean>(false);

    const getProfiles = async (profileType, profileName) => {
        try {
            if (profileType === "OWNER") {
                const profileList = await API.getProfiles({type: profileType, name: profileName});
                setProfiles(profileList);
            }else {
                const profileList = await API.getProfiles({type: profileType});
                setVets(profileList);
            }
        } catch (err) {
            console.log(err);
        }
    };

    const getAnimals = async () => {
        try {
            const animalList = await API.getAnimal(user.userId);
            setAnimals(animalList);
        }catch (err) {
            console.log(err);
        }
    };
    React.useEffect(()=>{
        async function fetchVets () {
            let param = {
                type: "VET",
                clientTimeZone: timeZone,
                rangeBegins: moment(time).format("YYYY-MM-DD"),
                rangeEnds: moment(time).add(1, "days").format("YYYY-MM-DD"),
            };
            const profiles = await API.getProfiles(param);
            setVets(profiles);
        }
        fetchVets();
    }, [time, timeZone, selectedState]);
    React.useEffect(() => {
        if (queryProfile.length) {
            const timeOutId = setTimeout(() => getProfiles("OWNER", queryProfile), 500);
            return () => clearTimeout(timeOutId);
        }
    }, [queryProfile]);
    React.useEffect(() => {
        if (!vets) {
            getProfiles("VET", "");
        }
        if (user) {
            getAnimals();
        }
    }, [profiles, user, vets, timeZone, animal]);

    React.useEffect(() => {
        if (timeZone !== "") {
            moment.tz.setDefault(timeZone);
            setTime(moment(time).tz(timeZone));
        }
    }, [timeZone]);

    const handleSubmit = async () => {
        const body = {
		    "timesRequested": [
                `${moment(time).tz(timeZone).format()}[${timeZone}]`
		    ],
		    "timeZone": timeZone,
		    "vetUserId": vet.userId,
		    "animalId": animal.id,
		    "description": description,
		    "appointmentType": serviceType
        };
        const resp = await API.createAppointmentOnBehalfOfUser(user.userId, body);
        if (resp.status === 500){
            enqueueSnackbar("The vet is not normally scheduled during this time. Please add availability", {
                variant: "error",
                autoHideDuration: 4000
            });
        }
        else if (resp.status === 400) {
            enqueueSnackbar("Invalid Appiontment Request", {
                variant: "error",
                autoHideDuration: 4000
            });
        }
        else if (resp.status === 401) {
            enqueueSnackbar("Authorization token has expired, please refresh your page and login if necessary", {
                variant: "error",
                autoHideDuration: 4000
            });
        }
        else if (resp.status === 403) {
            enqueueSnackbar("Animal doesn't belong to authenticated user", {
                variant: "error",
                autoHideDuration: 4000
            });
        }
        else if (resp.status === 404) {
            enqueueSnackbar("Vet does not work during this timeslot", {
                variant: "error",
                autoHideDuration: 4000
            });
        }else if (resp.status === 200) {
            enqueueSnackbar("Success!", {
                variant: "success",
                autoHideDuration: 4000
            });
            setVet(null);
            setServiceType("");
            setSelectedState({});
            setAnimal(null);
            setTime(moment.utc());
            setTimeZone("UTC");
            setDescription("");
            setQueryProfile("");
            setAnimals({});
        }
    };

    return(
        <Card style={{padding: 10, marginTop: 20, maxWidth: 800}}>
            <Title title="Schedule Appointment for the User" />
            <CardContent>
                <Typography variant="h3" style={{marginBottom: 20}}>
                    Create an appointment
                </Typography>
                <Typography style={{marginBottom: 20}}>
						This form will walk you through creating a new appointment starting with finding the right Owner and their pet.
						Please pay attention to the instructions to ensure that you don't run into any errors.
                </Typography>
            </CardContent>
            <CardContent>
                <Typography gutterBottom>
                Select an Owner from the dropdown:
                </Typography>
                <Autocomplete
                    id="userProfile"
                    value={user}
                    onChange={(event, value) => setUser(value)}
                    options={profiles}
                    style={{marginBottom: 20}}
                    getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                    renderInput={(params) => <TextField {...params} variant="outlined" onChange={(event) => setQueryProfile(event.target.value)}/>}
                />
                {animals && animals?.length > 0 &&
					<>
					    <Typography gutterBottom>
                            Select the Animal for the appointment:
					    </Typography>
					    <Autocomplete
					        id="animalSelect"
					        value={animal}
					        onChange={(event, value) => setAnimal(value)}
					        options={animals}
					        style={{marginBottom: 20}}
					        getOptionLabel={(option) => `${option.name} (Breed: ${option.breed})`}
					        renderInput={(params) => <TextField {...params} variant="outlined" />}
					    />
					</>
                }
                {animals && animals?.length === 0 &&
                    <Typography gutterBottom>
                        This customer doesn't have an animal for their appointment. They need to create one first.
                    </Typography>
                }
                {!wrongAnimal && animal &&
                    <>
                    	<Typography gutterBottom>
                            Choose the appointment state. If it's an advice appointment choose any state.
                    	</Typography>
                    	<Autocomplete
                    		id="states"
                    		value={selectedState}
                    		onChange={(event, value) => setSelectedState(value)}
                    		options={states}
                    		style={{marginBottom: 20}}
                    		getOptionLabel={(option) => option.label}
                    		renderInput={(params) => <TextField {...params} variant="outlined" />}
                    	/>
                    </>
                }
                {!wrongAnimal && selectedState &&
                    servicesList.filter(service => ["ADVICE_CHAT", "ADVICE_VIDEO", "RX_VIDEO"].some(item => item === service)).map(service => {
                    	return(
                    		<Button
                                type="button"
                    			key={service}
                    			variant={serviceType === service ? "contained" : "outlined"}
                    			color={serviceType === service ? "primary" : "default"}
                    			onClick={() => {setServiceType(service);}}
                    			style={{marginTop: 10, marginRight: 10, border: "1px solid rgba(0, 0, 0, 0.23)", color: "inherit"}}
                    		>
                    			{service.replace("_", " ")}
                    		</Button>
                    	);
                    })
                }
                {!wrongAnimal && serviceType !== "" &&
                    <>
                    	<Typography style={{marginTop: 20, marginBottom: 20}}>
                            Choose the date, time, and time zone for the appointment.
                    	</Typography>
                    	<Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <ThemeProvider theme={materialTheme}>
                                    <DateTimePicker
                                        value={time}
                                        fullWidth={true}
                                        disablePast
                                        onChange={(date) => setTime(moment(date).tz(timeZone, true))}
                                        showTodayButton
                                        minutesStep={20}
                                        inputVariant="outlined"
                                    />
                                </ThemeProvider>
                    		</Grid>
                            <Grid item xs={12} sm={6}>
                    			<Autocomplete
                    				options={timezones}
                                    fullWidth={true}
                    				getOptionLabel={(option) => option}
                    				value={timeZone}
                    				onChange={(event, value) => {
                    					setTimeZone(value);
                    				}}
                    				renderInput={(params) => <TextField {...params} variant="outlined" />}
                    			/>
                    		</Grid>
                    	</Grid>
                        {!wrongAnimal && !!time &&
						<>
						    <Typography style={{marginTop: 20}} gutterBottom>
								Select the Vet you are working with. NOTE: If the vet is not normally scheduled, please add new availability using the "Add Vet Schedule" form.
						    </Typography>
						    <Autocomplete
						        id="vetProfile"
						        // disableClearable={true}
						        value={vet}
						        onChange={(event, value) => setVet(value)}
						        options={vets}
						        style={{marginBottom: 20}}
						        getOptionLabel={(option) => `Dr. ${option.firstName} ${option.lastName}`}
						        renderInput={(params) => <TextField {...params} variant="outlined" />}
						    />
						</>
                        }
                        {!!vet && <><Typography style={{marginTop: 20}} gutterBottom>
							Enter the Appointment Description from the user.
                        </Typography>
                        <TextField
                            multiline
                            fullWidth
                            variant="outlined"
                            value={description}
                            id="description"
                            onChange={event => {event.preventDefault(); setDescription(event.target.value);}}
                        /></>}
                    </>
                }
                {!wrongAnimal && description !== "" &&
                    <Button
                        type="button"
                    	variant="contained"
                    	onClick={() => handleSubmit()}
                    	color="primary"
                    	style={{marginTop: 30}}
                    >
                        Submit!
                    </Button>
                }
            </CardContent>
        </Card>
    );
};

export default ScheduleAppointment;
